import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import {
  fetchVersions, fetchComments, addComment, resolveComment, deleteComment,
  restoreVersion, applyHold, liftHold, setAvailability, createLink, updateLink, deleteLink, fetchLinks, fetchLinkStats,
  trashItem, getContentUrl, downloadFile, fetchFolderById, fetchTags, createTag, attachTag, detachTag,
  fetchPeopleDirectory, addShare, removeShare, fetchShares, updateSharePermission, fetchItem,
  requestPermission, fetchMyPermissionRequests, fetchIncomingPermissionRequests, decidePermissionRequest,
  type CreateLinkOptions, type UpdateLinkOptions,
} from '../services/api';
import { cacheItemOffline, evictItemOffline, isCachedOffline } from '../services/offlineCache';
import type { DriveItem, Tag, Person, ShareGrant, SharedLink, LinkStats, SharePermission, PermissionRequest } from '../types';
import { hasPermissionAtLeast } from '../types';
import Button from '../components/Button';
import Tabs, { TabItem } from '../components/Tabs';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import { Thumbnail } from '../components/TypeGlyph';
import { useAppStore } from '../store/useAppStore';

const PRESET_TAGS: { name: string; color: string }[] = [
  { name: 'Important', color: '#E4572E' },
  { name: 'Review', color: '#D8A31A' },
  { name: 'Final', color: '#2E9E5B' },
  { name: 'Draft', color: '#8A8F98' },
  { name: 'Confidential', color: '#8B3FE0' },
];

// The four sharing tiers, in the order they should list in a dropdown —
// ascending privilege, matching the drive_permission rank in access.ts.
const PERMISSION_OPTIONS: { value: SharePermission; label: string; hint: string }[] = [
  { value: 'viewer', label: 'View', hint: 'Can open, preview and download' },
  { value: 'commenter', label: 'View & Comment', hint: 'Everything in View, plus commenting' },
  { value: 'editor', label: 'Edit', hint: 'Can also edit, rename, move and upload new versions' },
  { value: 'manager', label: 'Full Share', hint: 'Everything in Edit, plus managing sharing' },
];
const PERMISSION_LABEL: Record<SharePermission, string> = {
  viewer: 'View', commenter: 'View & Comment', editor: 'Edit', manager: 'Full Share',
};

const DETAIL_TABS: TabItem[] = [
  { id: 'details', label: 'Details' },
  { id: 'comments', label: 'Comments' },
  { id: 'versions', label: 'Versions' },
  { id: 'sharing', label: 'Sharing' },
  { id: 'activity', label: 'Activity' },
];

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function relativeTime(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function mimeLabel(mimeCategory?: string | null, name?: string): string {
  const ext = name ? name.split('.').pop()?.toUpperCase() ?? '' : '';
  const map: Record<string, string> = {
    image: `${ext} Image`,
    video: `${ext} Video`,
    audio: `${ext} Audio`,
    text: `${ext} Text`,
    application: ext ? `${ext} File` : 'File',
  };
  return map[mimeCategory ?? 'application'] ?? (ext ? `${ext} File` : 'File');
}

interface Props {
  item: DriveItem;
  onClose: () => void;
  onOpen?: () => void;
  isAdmin?: boolean;
  spaceId?: string;
  initialTab?: string;
}

export default function DetailsPanel({ item, onClose, onOpen, isAdmin, spaceId, initialTab = 'details' }: Props) {
  const { toast, userId, email: ownerEmail, displayName } = useAppStore();
  const qc = useQueryClient();
  const [tab, setTab] = useState(initialTab);
  const [commentBody, setCommentBody] = useState('');
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<string | null>(null);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);

  const { data: allTags = [] } = useQuery({ queryKey: qk.tags(), queryFn: fetchTags });

  // The `item` prop comes from whatever list fetched it and never carries
  // my_permission (only the single-item endpoint does) — fetch it fresh so
  // the comment gate and the manager-only "incoming requests" section have a
  // real answer instead of guessing from stale list data.
  const { data: itemDetail } = useQuery({ queryKey: qk.item(item.id), queryFn: () => fetchItem(item.id) });
  const myPermission = itemDetail?.my_permission;
  const canComment = hasPermissionAtLeast(myPermission, 'commenter');
  const canManageSharing = hasPermissionAtLeast(myPermission, 'manager');
  // The owner is item.owner_user_id — distinct from "has manager tier",
  // which a delegate also has. Whoever's viewing sees the real owner in the
  // Sharing tab's owner row, not themselves mislabeled as one.
  const isCurrentUserOwner = item.owner_user_id === userId;

  const { data: folder } = useQuery({
    queryKey: ['folder', item.folder_id],
    queryFn: () => fetchFolderById(item.folder_id!),
    enabled: !!item.folder_id,
  });

  const handleOpen = () => {
    if (onOpen) { onOpen(); }
    else {
      getContentUrl(item.id).then(url => window.open(url, '_blank')).catch((e: unknown) => {
        toast(e instanceof Error ? e.message : 'Could not open file', 'danger');
      });
    }
  };

  const handleDownload = async () => {
    try {
      await downloadFile(item.id, item.name);
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Could not download file', 'danger');
    }
  };

  const { data: versions = [] } = useQuery({
    queryKey: qk.itemVersions(item.id),
    queryFn: () => fetchVersions(item.id),
    enabled: tab === 'versions',
  });

  const { data: comments = [] } = useQuery({
    queryKey: qk.itemComments(item.id),
    queryFn: () => fetchComments(item.id),
    enabled: tab === 'comments',
  });

  const { data: shares = [] } = useQuery({
    queryKey: qk.itemShares(item.id),
    queryFn: () => fetchShares(item.id),
    enabled: tab === 'sharing',
  });

  const commentMutation = useMutation({
    mutationFn: (body: string) => addComment(item.id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.itemComments(item.id) }); setCommentBody(''); },
  });

  const holdMutation = useMutation({
    mutationFn: (reason: string) => applyHold(item.id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['items'] }); setShowHoldModal(false); toast('Hold applied'); },
  });

  const liftHoldMutation = useMutation({
    mutationFn: () => liftHold(item.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['items'] }); toast('Hold lifted'); },
  });

  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineCached, setOfflineCached] = useState(false);

  useEffect(() => {
    isCachedOffline(item.id).then(setOfflineCached);
  }, [item.id]);

  const availMutation = useMutation({
    mutationFn: async (av: 'online_only' | 'offline') => {
      if (av === 'offline') {
        setOfflineBusy(true);
        try {
          const url = await getContentUrl(item.id);
          await cacheItemOffline(item.id, url);
          setOfflineCached(true);
        } finally {
          setOfflineBusy(false);
        }
      } else {
        await evictItemOffline(item.id);
        setOfflineCached(false);
      }
      return setAvailability(item.id, av);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const { data: people = [] } = useQuery({ queryKey: qk.people(), queryFn: fetchPeopleDirectory, enabled: tab === 'sharing' || tab === 'comments' });
  const personLabel = (userId: string) => {
    const p = people.find((person: Person) => person.id === userId);
    return p?.name ?? p?.email ?? userId;
  };
  const ownerLabel = personLabel(item.owner_user_id);
  const [pickedPersonId, setPickedPersonId] = useState('');
  const [pickedPermission, setPickedPermission] = useState<SharePermission>('viewer');

  const addShareMutation = useMutation({
    mutationFn: (permission: SharePermission) => addShare(item.id, pickedPersonId, permission),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.itemShares(item.id) });
      setPickedPersonId('');
      setPickedPermission('viewer');
      toast('Shared');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({ shareId, permission }: { shareId: string; permission: SharePermission }) => updateSharePermission(shareId, permission),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.itemShares(item.id) }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const removeShareMutation = useMutation({
    mutationFn: (shareId: string) => removeShare(shareId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.itemShares(item.id) }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  // Permission requests: "ask the owner to raise my tier" from the low-access
  // side, and "review incoming asks" from the manager side.
  const { data: myRequests = [] } = useQuery({
    queryKey: qk.myPermissionRequests(item.id),
    queryFn: () => fetchMyPermissionRequests(item.id),
    enabled: tab === 'comments' || tab === 'sharing',
  });
  const myPendingRequest = myRequests.find(r => r.status === 'pending') ?? null;

  const requestPermissionMutation = useMutation({
    mutationFn: ({ permission, reason }: { permission: SharePermission; reason?: string }) => requestPermission(item.id, permission, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.myPermissionRequests(item.id) }); toast('Request sent'); },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const { data: incomingRequests = [] } = useQuery({
    queryKey: qk.incomingPermissionRequests(item.id),
    queryFn: () => fetchIncomingPermissionRequests(item.id),
    enabled: tab === 'sharing' && canManageSharing,
  });

  const decideRequestMutation = useMutation({
    mutationFn: ({ requestId, decision }: { requestId: string; decision: 'approve' | 'deny' }) => decidePermissionRequest(requestId, decision),
    onSuccess: (_, { decision }) => {
      qc.invalidateQueries({ queryKey: qk.incomingPermissionRequests(item.id) });
      qc.invalidateQueries({ queryKey: qk.itemShares(item.id) });
      toast(decision === 'approve' ? 'Access granted' : 'Request denied');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  // External share links. An item can carry several at once, each separately
  // named — fetched fresh rather than trusting the possibly-stale `item.link`
  // embed, so create/update/delete reflect immediately without a local-state
  // patch to paper over staleness.
  const { data: links = [] } = useQuery({ queryKey: qk.itemLinks(item.id), queryFn: () => fetchLinks(item.id), enabled: tab === 'sharing' });

  const [showCreateLink, setShowCreateLink] = useState(false);
  const [manageLink, setManageLink] = useState<SharedLink | null>(null);
  const [deleteLinkTarget, setDeleteLinkTarget] = useState<SharedLink | null>(null);
  const [statsLink, setStatsLink] = useState<SharedLink | null>(null);
  const [linkActionsMenu, setLinkActionsMenu] = useState<{ x: number; y: number; link: SharedLink } | null>(null);

  const createLinkMutation = useMutation({
    mutationFn: (opts: CreateLinkOptions) => createLink(item.id, opts),
    onSuccess: (link) => {
      qc.invalidateQueries({ queryKey: qk.itemLinks(item.id) });
      setShowCreateLink(false);
      navigator.clipboard.writeText(`${window.location.origin}/s/${link.token}`).catch(() => {});
      toast('Link created — copied to clipboard');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const updateLinkMutation = useMutation({
    mutationFn: ({ linkId, opts }: { linkId: string; opts: UpdateLinkOptions }) => updateLink(item.id, linkId, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.itemLinks(item.id) });
      toast('Link updated');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const delLinkMutation = useMutation({
    mutationFn: (linkId: string) => deleteLink(item.id, linkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.itemLinks(item.id) });
      setManageLink(null);
      setDeleteLinkTarget(null);
      toast('Link deleted');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const { data: linkStats } = useQuery({
    queryKey: qk.itemLinkStats(item.id, statsLink?.id ?? ''),
    queryFn: () => fetchLinkStats(item.id, statsLink!.id),
    enabled: !!statsLink,
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) => restoreVersion(item.id, versionId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['items'] }); setShowRestoreConfirm(null); toast('Version restored'); },
  });

  const attachTagMutation = useMutation({
    mutationFn: (tagId: string) => attachTag(item.id, tagId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const detachTagMutation = useMutation({
    mutationFn: (tagId: string) => detachTag(item.id, tagId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  // Resolve a preset name to an org tag id, creating it on first use. allTags
  // (fetched once per org) is checked first so repeated use of a preset never
  // creates duplicate tags.
  async function resolveTagId(preset: { name: string; color: string }): Promise<string> {
    const existing = allTags.find((t: Tag) => t.name === preset.name);
    if (existing) return existing.id;
    const created = await createTag(preset.name, preset.color);
    qc.invalidateQueries({ queryKey: qk.tags() });
    return created.id;
  }

  async function toggleTag(preset: { name: string; color: string }) {
    const tagId = await resolveTagId(preset);
    const hasTag = (item.tags ?? []).some((t) => t.id === tagId);
    if (hasTag) detachTagMutation.mutate(tagId);
    else attachTagMutation.mutate(tagId);
  }

  function removeTag(tagId: string) {
    detachTagMutation.mutate(tagId);
  }

  const isHeld = item.hold_active;
  const isTrashed = item.state === 'trashed';

  return (
    <div className="t-panel">
      {/* Header */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Thumbnail item={item} width={36} height={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{mimeLabel(item.mime_category, item.name)}</div>
        </div>
        <Button kind="ghost" size="xs" icon="x" iconOnly onClick={onClose} />
      </div>

      {/* Tabs */}
      <Tabs tabs={DETAIL_TABS} value={tab} onChange={setTab} />

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'details' && (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Preview */}
            <div style={{ borderRadius: 10, overflow: 'hidden', background: 'var(--paper-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
              <Thumbnail item={item} width={352} height={160} />
            </div>

            {/* Meta rows */}
            <div>
              <DetailRow label="Type" value={mimeLabel(item.mime_category, item.name)} />
              <DetailRow label="Size" value={item.size_bytes ? formatBytes(Number(item.size_bytes)) : '—'} mono />
              <DetailRow label="Location" value={folder ? folder.name : 'My Drive'} />
              <DetailRow label="Owner">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar name={displayName || 'You'} size="xs" style={{ width: 20, height: 20, fontSize: 8 }} />
                  <span>You</span>
                </div>
              </DetailRow>
              <DetailRow
                label="Modified"
                value={`${new Date(item.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${item.currentVersion ? ` · v${item.currentVersion.version_no}` : ''}`}
              />
              <DetailRow label="Last used" value={relativeTime(item.last_accessed_at ?? item.updated_at)} />
              {item.scan_status && (
                <DetailRow label="Scan">
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999, fontSize: 'var(--fs-xs)', fontWeight: 600,
                    background: item.scan_status === 'clean' ? 'var(--success-soft)' : item.scan_status === 'infected' ? 'var(--danger-soft)' : 'var(--paper-3)',
                    color: item.scan_status === 'clean' ? 'var(--success-ink)' : item.scan_status === 'infected' ? 'var(--danger)' : 'var(--ink-3)',
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
                    {item.scan_status === 'clean' ? 'Clean' : item.scan_status === 'infected' ? 'Infected' : item.scan_status}
                  </span>
                </DetailRow>
              )}
              {item.sha && (
                <DetailRow label="SHA-256" value={`${item.sha.slice(0, 18)}…`} mono />
              )}
            </div>

            {/* Tags */}
            {((item.tags ?? []).length > 0 || true) && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', marginBottom: 8 }}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {(item.tags ?? []).map(t => (
                    <span
                      key={t.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 999, fontSize: 'var(--fs-xs)', fontWeight: 500,
                        background: t.color ? `${t.color}22` : 'var(--paper-3)',
                        border: `1px solid ${t.color ? `${t.color}44` : 'var(--line)'}`,
                        color: t.color ?? 'var(--ink-2)',
                      }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.color ?? 'var(--ink-4)', flexShrink: 0 }} />
                      {t.name}
                      <button
                        onClick={() => removeTag(t.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit', opacity: 0.6, marginLeft: 2, fontSize: 11 }}
                      >×</button>
                    </span>
                  ))}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setTagMenuOpen((v) => !v)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: 'var(--fs-xs)', fontWeight: 500, background: 'none', border: '1.5px dashed var(--line)', cursor: 'pointer', color: 'var(--ink-3)' }}
                    >
                      <Icon name="plus" size={11} /> Tag
                    </button>
                    {tagMenuOpen && (
                      <>
                        <div onClick={() => setTagMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 51, minWidth: 160, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', boxShadow: 'var(--shadow-lg)', padding: 4 }}>
                          {PRESET_TAGS.map((preset) => {
                            const applied = (item.tags ?? []).some((t) => t.name === preset.name);
                            return (
                              <button
                                key={preset.name}
                                onClick={() => { toggleTag(preset); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', textAlign: 'left', fontFamily: 'var(--font-sans)' }}
                              >
                                <span style={{ width: 8, height: 8, borderRadius: 999, background: preset.color, flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>{preset.name}</span>
                                {applied && <Icon name="check" size={12} color="var(--accent)" />}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Online-only toggle card */}
            <div style={{ padding: '12px 14px', background: 'var(--paper-2)', borderRadius: 10, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="cloud" size={18} color="var(--ink-3)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Online-only</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  {offlineBusy
                    ? 'Downloading for offline use…'
                    : item.availability === 'online_only'
                    ? 'Streams on demand — saves local space'
                    : offlineCached
                    ? 'Downloaded — opens without a connection'
                    : 'Marked for offline, but not downloaded on this device yet'}
                </div>
              </div>
              <ToggleSwitch
                checked={item.availability === 'online_only'}
                disabled={offlineBusy}
                onChange={v => availMutation.mutate(v ? 'online_only' : 'offline')}
              />
            </div>

            {/* Retention hold card */}
            <div style={{ padding: '12px 14px', background: isHeld ? 'var(--warn-soft)' : 'var(--paper-2)', borderRadius: 10, border: '1px solid', borderColor: isHeld ? 'var(--warn-line)' : 'var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="lock" size={18} color={isHeld ? 'var(--warn)' : 'var(--ink-3)'} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', color: isHeld ? 'var(--warn)' : 'var(--ink)' }}>Retention hold</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: isHeld ? 'var(--warn)' : 'var(--ink-3)' }}>
                  {isHeld ? (item.hold_reason ?? 'Active') : 'Off'}
                </div>
              </div>
              {isAdmin ? (
                <ToggleSwitch
                  checked={isHeld}
                  onChange={v => v ? setShowHoldModal(true) : liftHoldMutation.mutate()}
                />
              ) : (
                <ToggleSwitch checked={isHeld} onChange={() => {}} disabled />
              )}
            </div>
          </div>
        )}

        {tab === 'comments' && (
          <div style={{ padding: 14 }}>
            <div style={{ marginBottom: 12 }}>
              {itemDetail === undefined ? (
                // Don't flash "Requires commenter permission" while the
                // effective-permission fetch is still in flight.
                <div className="t-skeleton" style={{ height: 76, borderRadius: 8 }} />
              ) : canComment ? (
                <>
                  <textarea
                    className="t-input"
                    placeholder="Add a comment…"
                    rows={3}
                    value={commentBody}
                    onChange={e => setCommentBody(e.target.value)}
                    style={{ resize: 'none', fontSize: 'var(--fs-sm)' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                    <Button kind="accent" size="sm" loading={commentMutation.isPending} onClick={() => { if (commentBody.trim()) commentMutation.mutate(commentBody.trim()); }}>
                      Post
                    </Button>
                  </div>
                </>
              ) : (
                <div className="t-alert t-alert--warn" style={{ alignItems: 'flex-start', gap: 10 }}>
                  <Icon name="lock" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    Requires <strong>commenter</strong> permission — you have <strong>{PERMISSION_LABEL[myPermission ?? 'viewer']}</strong>.
                    Request access from the <strong>Sharing</strong> tab.
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {comments.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', paddingTop: 20 }}>No comments yet</div>
              )}
              {comments.filter(c => !c.resolved).map(c => {
                const author = people.find((p: Person) => p.id === c.author_id);
                const authorLabel = c.author_id === userId ? (displayName || 'You') : (author?.name ?? author?.email ?? c.author_name ?? c.author_id);
                return (
                <div key={c.id} style={{ display: 'flex', gap: 8 }}>
                  <Avatar name={authorLabel} size="xs" />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600 }}>{authorLabel}</span>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-4)' }}>{relativeTime(c.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink)', lineHeight: 1.5 }}>{c.body}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button
                        style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => resolveComment(item.id, c.id).then(() => qc.invalidateQueries({ queryKey: qk.itemComments(item.id) }))}
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'versions' && (
          <div style={{ padding: 14 }}>
            <div className="t-alert t-alert--info" style={{ marginBottom: 12, fontSize: 'var(--fs-xs)' }}>
              <Icon name="info" size={13} />
              {versions.length} version{versions.length !== 1 ? 's' : ''} stored
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {versions.map(v => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, background: v.is_current ? 'var(--accent-soft)' : 'var(--paper-2)', borderRadius: 8, border: '1px solid', borderColor: v.is_current ? 'rgba(45,92,246,0.2)' : 'var(--line)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: v.is_current ? 600 : 400 }}>
                      v{v.version_no} {v.is_current && <Chip tone="accent" size="sm">Current</Chip>}
                    </div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                      {formatBytes(v.size_bytes)} · {relativeTime(v.created_at)}
                    </div>
                  </div>
                  <Button kind="ghost" size="xs" icon="download">Download</Button>
                  {!v.is_current && (
                    <Button kind="outline" size="xs" onClick={() => setShowRestoreConfirm(v.id)}>Restore</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'sharing' && (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Delegated sharing: only the owner or someone granted 'manager'
                (owner or delegate — the check is rank-based, not identity-based,
                see access.ts) may open or use any of these controls. Below
                'manager', the tab is read-only: who has access and whether a
                link exists are visible, but nothing here is editable. */}
            {itemDetail !== undefined && !canManageSharing && (
              <RequestAccessCard
                myPermission={myPermission}
                pendingRequest={myPendingRequest}
                sending={requestPermissionMutation.isPending}
                onRequest={(permission, reason) => requestPermissionMutation.mutate({ permission, reason })}
              />
            )}

            {/* External public share links — an item can carry several,
                each separately named (Zoho-style). */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>External links</div>
                {canManageSharing && (
                  <Button kind="outline" size="sm" icon="link" onClick={() => setShowCreateLink(true)}>Create link</Button>
                )}
              </div>

              {links.length === 0 ? (
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', padding: '8px 0' }}>
                  {canManageSharing ? 'No external links yet — anyone with a link can open the file, no account needed.' : 'No link has been created for this file'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {links.map(link => (
                    <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--paper-2)', borderRadius: 10, border: '1px solid var(--line)' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <Icon name="link" size={15} color="var(--accent)" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{link.name || 'Untitled link'}</span>
                          <Chip tone="accent" size="sm">{PERMISSION_LABEL[link.permission]} access</Chip>
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Created by {personLabel(link.created_by)} on {formatDateTime(link.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <Button kind="outline" size="sm" icon="copy" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/s/${link.token}`).then(() => toast('Copied!'))}>
                          Copy
                        </Button>
                        {canManageSharing && (
                          <Button
                            kind="outline"
                            size="sm"
                            icon="settings"
                            onClick={e => setLinkActionsMenu({ x: e.clientX, y: e.clientY, link })}
                          >
                            Actions
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {linkActionsMenu && (
              <ContextMenu
                x={linkActionsMenu.x}
                y={linkActionsMenu.y}
                onClose={() => setLinkActionsMenu(null)}
                items={([
                  { label: 'View stats', icon: 'chart', onClick: () => setStatsLink(linkActionsMenu.link) },
                  { label: 'Email share link', icon: 'mail', disabled: true },
                  { label: 'Change settings', icon: 'settings', onClick: () => setManageLink(linkActionsMenu.link) },
                  { label: 'Delete share link', icon: 'trash', danger: true, onClick: () => setDeleteLinkTarget(linkActionsMenu.link) },
                ]) as ContextMenuItem[]}
              />
            )}

            {/* Incoming permission requests — manager only */}
            {canManageSharing && (
              <IncomingRequests
                requests={incomingRequests}
                deciding={decideRequestMutation.isPending}
                onDecide={(requestId, decision) => decideRequestMutation.mutate({ requestId, decision })}
              />
            )}

            {/* Internal organization sharing */}
            <div>
              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 10 }}>Who can access</div>
              {canManageSharing && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <select
                    className="t-input"
                    style={{ flex: 1 }}
                    value={pickedPersonId}
                    onChange={e => setPickedPersonId(e.target.value)}
                  >
                    <option value="" disabled>{people.length === 0 ? 'No people available' : 'Add people or groups…'}</option>
                    {people.map((p: Person) => (
                      <option key={p.id} value={p.id}>{p.email ? `${p.name} — ${p.email}` : p.name}</option>
                    ))}
                  </select>
                  <select
                    className="t-input"
                    style={{ width: 150, flexShrink: 0 }}
                    value={pickedPermission}
                    onChange={e => setPickedPermission(e.target.value as SharePermission)}
                  >
                    {PERMISSION_OPTIONS
                      // A delegated manager may grant at most their own tier
                      // (server-enforced too — this just avoids offering an
                      // option that would 403).
                      .filter(p => hasPermissionAtLeast(myPermission, p.value))
                      .map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                  <Button
                    kind="accent"
                    size="sm"
                    icon="plus"
                    disabled={!pickedPersonId}
                    loading={addShareMutation.isPending}
                    onClick={() => pickedPersonId && addShareMutation.mutate(pickedPermission)}
                  >Add</Button>
                </div>
              )}

              {/* Owner row — the actual owner, not whoever is currently
                  viewing (a delegated manager sees the real owner here too). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                <Avatar name={isCurrentUserOwner ? (displayName || 'You') : ownerLabel} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>{isCurrentUserOwner ? 'You' : ownerLabel}</div>
                  {isCurrentUserOwner && (
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ownerEmail || userId}</div>
                  )}
                </div>
                <Chip tone="success" size="sm">Owner</Chip>
              </div>

              {/* Shared users */}
              {shares.length === 0 ? (
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', padding: '10px 0' }}>Private — not shared with anyone yet.</div>
              ) : (
                shares.map((s: ShareGrant) => {
                  const person = people.find((p: Person) => p.id === s.grantee_id);
                  const primary = person?.email ?? person?.name ?? s.grantee_id;
                  const secondary = person?.email && person?.name ? person.name : null;
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                      <Avatar name={person?.name ?? primary} size="sm" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</div>
                        {secondary && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{secondary}</div>}
                      </div>
                      {canManageSharing ? (
                        <>
                          <select
                            className="t-input t-input--sm"
                            style={{ width: 140, flexShrink: 0 }}
                            value={s.permission}
                            onChange={e => updatePermissionMutation.mutate({ shareId: s.id, permission: e.target.value as SharePermission })}
                          >
                            {PERMISSION_OPTIONS
                              // Same ceiling as the add-flow — plus the grant's
                              // current tier, so a delegate can still see (if
                              // not raise) a tier that happens to exceed their
                              // own, e.g. one the owner granted directly.
                              .filter(p => hasPermissionAtLeast(myPermission, p.value) || p.value === s.permission)
                              .map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                          <Button kind="ghost" size="xs" icon="x" iconOnly onClick={() => removeShareMutation.mutate(s.id)} title="Remove access" />
                        </>
                      ) : (
                        <Chip tone="neutral" size="sm">{PERMISSION_LABEL[s.permission]}</Chip>
                      )}
                    </div>
                  );
                })
              )}

              {/* Info note */}
              <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--accent-soft)', borderRadius: 8, border: '1px solid rgba(45,92,246,0.15)', display: 'flex', gap: 8 }}>
                <Icon name="info" size={14} color="var(--accent)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--accent-ink)', lineHeight: 1.5 }}>
                  Everyone in <strong>My Drive</strong> can already find files in this space through their membership.
                </span>
              </div>
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {versions.map(v => (
                <div key={v.id} style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-sm)' }}>
                  <Icon name="file" size={14} color="var(--ink-3)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <span style={{ fontWeight: 500 }}>Version {v.version_no}</span>{' '}
                    <span style={{ color: 'var(--ink-3)' }}>uploaded · {relativeTime(v.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, flexShrink: 0 }}>
        <Button kind="accent" size="sm" icon="eye" block onClick={handleOpen}>Open</Button>
        <Button kind="outline" size="sm" icon="download" onClick={handleDownload}>Download</Button>
        <Button kind="ghost" size="sm" icon="trash" disabled={isHeld || isTrashed}
          onClick={() => { trashItem(item.id).then(() => { qc.invalidateQueries({ queryKey: ['items'] }); onClose(); toast('Moved to trash'); }).catch((e: Error) => toast(e.message, 'danger')); }}>
          Trash
        </Button>
      </div>

      {/* Hold modal */}
      <Modal
        open={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        title="Apply retention hold"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowHoldModal(false)}>Cancel</Button>
            <Button kind="accent" loading={holdMutation.isPending} onClick={() => { if (holdReason.trim()) holdMutation.mutate(holdReason.trim()); }}>Apply hold</Button>
          </>
        }
      >
        <div className="t-stack">
          <div className="t-alert t-alert--warn">
            <Icon name="warn" size={14} />
            Held files cannot be renamed, moved, or deleted.
          </div>
          <div className="t-field">
            <label>Reason for hold</label>
            <textarea className="t-input" rows={3} value={holdReason} onChange={e => setHoldReason(e.target.value)} placeholder="Legal hold, compliance audit…" />
          </div>
        </div>
      </Modal>

      {/* Version restore confirm */}
      <Modal
        open={!!showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(null)}
        title="Restore version"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowRestoreConfirm(null)}>Cancel</Button>
            <Button kind="accent" loading={restoreMutation.isPending} onClick={() => { if (showRestoreConfirm) restoreMutation.mutate(showRestoreConfirm); }}>Restore</Button>
          </>
        }
      >
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
          This will create a new version as the current one. The existing versions are preserved.
        </p>
      </Modal>

      {/* Create external share link */}
      <CreateLinkModal
        open={showCreateLink}
        itemName={item.name}
        loading={createLinkMutation.isPending}
        onClose={() => setShowCreateLink(false)}
        onCreate={opts => createLinkMutation.mutate(opts)}
      />

      {/* Manage / edit an existing external share link */}
      {manageLink && (
        <ManageLinkModal
          open={!!manageLink}
          link={manageLink}
          itemName={item.name}
          updating={updateLinkMutation.isPending}
          onClose={() => setManageLink(null)}
          onUpdate={opts => updateLinkMutation.mutate({ linkId: manageLink.id, opts })}
          onRequestDelete={() => { setDeleteLinkTarget(manageLink); setManageLink(null); }}
        />
      )}

      {/* Delete link confirm */}
      <Modal
        open={!!deleteLinkTarget}
        onClose={() => setDeleteLinkTarget(null)}
        title="Delete share link"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setDeleteLinkTarget(null)}>Cancel</Button>
            <Button kind="danger" loading={delLinkMutation.isPending} onClick={() => { if (deleteLinkTarget) delLinkMutation.mutate(deleteLinkTarget.id); }}>Delete</Button>
          </>
        }
      >
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
          The link stops working immediately. Anyone who has it will no longer be able to open this file.
        </p>
      </Modal>

      {/* External link Access Stats */}
      {statsLink && (
        <LinkStatsModal
          open={!!statsLink}
          link={statsLink}
          itemName={item.name}
          stats={linkStats}
          onClose={() => setStatsLink(null)}
        />
      )}
    </div>
  );
}

function DetailRow({ label, value, mono, children }: { label: string; value?: string; mono?: boolean; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--fs-sm)', padding: '8px 0' }}>
      <span style={{ color: 'var(--ink-3)', flexShrink: 0, width: 100 }}>{label}</span>
      {children ?? (
        <span style={{ color: 'var(--ink)', fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: mono ? 11 : undefined }}>{value}</span>
      )}
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div
      onClick={() => !disabled && onChange(!checked)}
      style={{
        position: 'relative', width: 40, height: 22, borderRadius: 999, flexShrink: 0,
        background: checked ? 'var(--accent)' : 'var(--paper-3)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'background var(--t-fast)',
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3, width: 16, height: 16,
        borderRadius: 999, background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left var(--t-fast)',
      }} />
    </div>
  );
}

// Local datetime-input <-> ISO helpers. <input type="datetime-local"> works in
// the browser's local time with no timezone suffix; the API wants a real ISO
// instant, so the two need converting at the boundary, not stored interchangeably.
function toDatetimeLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromDatetimeLocal(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

// Shared body for the create/manage link forms — permission + password/expiry/
// download/print toggles. Only the footer and initial values differ between
// "Create External Share Link" and the "Link Settings" section of Manage.
function LinkFields({ passwordOn, setPasswordOn, password, setPassword, expiryOn, setExpiryOn, expiresAt, setExpiresAt, allowDownload, setAllowDownload, allowPrint, setAllowPrint }: {
  passwordOn: boolean; setPasswordOn: (v: boolean) => void;
  password: string; setPassword: (v: string) => void;
  expiryOn: boolean; setExpiryOn: (v: boolean) => void;
  expiresAt: string; setExpiresAt: (v: string) => void;
  allowDownload: boolean; setAllowDownload: (v: boolean) => void;
  allowPrint: boolean; setAllowPrint: (v: boolean) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="t-stack">
      <div className="t-field">
        <label>Permission</label>
        {/* External links are anonymous — no account needed — so access is
            locked to View only. Comment/edit/manage requires an internal
            share with a known org member instead (server-enforced too). */}
        <div className="t-input" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', cursor: 'default' }}>
          <Icon name="lock" size={12} />
          View only
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
        <ToggleSwitch checked={passwordOn} onChange={setPasswordOn} />
        <span style={{ fontSize: 'var(--fs-sm)', flex: 1, cursor: 'pointer' }} onClick={() => setPasswordOn(!passwordOn)}>Password protection</span>
      </div>
      {passwordOn && (
        <div className="t-field" style={{ marginTop: -6 }}>
          <div style={{ position: 'relative' }}>
            <input
              className="t-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter a password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ paddingRight: 36 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', display: 'grid', placeItems: 'center' }}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={15} />
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
        <ToggleSwitch checked={expiryOn} onChange={setExpiryOn} />
        <span style={{ fontSize: 'var(--fs-sm)', flex: 1, cursor: 'pointer' }} onClick={() => setExpiryOn(!expiryOn)}>Expiration date</span>
      </div>
      {expiryOn && (
        <div className="t-field" style={{ marginTop: -6 }}>
          <input
            className="t-input"
            type="datetime-local"
            value={expiresAt}
            min={toDatetimeLocal(new Date().toISOString())}
            onChange={e => setExpiresAt(e.target.value)}
          />
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 4 }}>
            The link stops working automatically after this time.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
        <ToggleSwitch checked={allowDownload} onChange={setAllowDownload} />
        <span style={{ fontSize: 'var(--fs-sm)', flex: 1, cursor: 'pointer' }} onClick={() => setAllowDownload(!allowDownload)}>Allow download</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
        <ToggleSwitch checked={allowPrint} onChange={setAllowPrint} />
        <span style={{ fontSize: 'var(--fs-sm)', flex: 1, cursor: 'pointer' }} onClick={() => setAllowPrint(!allowPrint)}>Allow print</span>
      </div>
    </div>
  );
}

function CreateLinkModal({ open, itemName, loading, onClose, onCreate }: {
  open: boolean; itemName: string; loading: boolean;
  onClose: () => void; onCreate: (opts: CreateLinkOptions) => void;
}) {
  const [name, setName] = useState('');
  const [passwordOn, setPasswordOn] = useState(false);
  const [password, setPassword] = useState('');
  const [expiryOn, setExpiryOn] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [allowPrint, setAllowPrint] = useState(true);

  // Reset to defaults each time the modal is (re-)opened, so a previous
  // attempt's password/expiry never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setName('');
    setPasswordOn(false);
    setPassword('');
    setExpiryOn(false);
    setExpiresAt('');
    setAllowDownload(true);
    setAllowPrint(true);
  }, [open]);

  const canCreate = !passwordOn || password.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create External Share Link"
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button
            kind="accent"
            loading={loading}
            disabled={!canCreate}
            onClick={() => onCreate({
              scope: 'anyone',
              permission: 'viewer',
              allow_download: allowDownload,
              allow_print: allowPrint,
              ...(name.trim() ? { name: name.trim() } : {}),
              ...(passwordOn && password ? { password } : {}),
              ...(expiryOn ? { expires_at: fromDatetimeLocal(expiresAt) } : {}),
            })}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="t-stack">
        <div className="t-alert t-alert--info" style={{ alignItems: 'flex-start', gap: 10 }}>
          <Icon name="link" size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Anyone with the link can open <strong>{itemName}</strong> — no account needed.</span>
        </div>
        <div className="t-field">
          <label>Link name (optional)</label>
          <input
            className="t-input"
            placeholder="e.g. Client review"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={255}
          />
        </div>
        <LinkFields
          passwordOn={passwordOn} setPasswordOn={setPasswordOn}
          password={password} setPassword={setPassword}
          expiryOn={expiryOn} setExpiryOn={setExpiryOn}
          expiresAt={expiresAt} setExpiresAt={setExpiresAt}
          allowDownload={allowDownload} setAllowDownload={setAllowDownload}
          allowPrint={allowPrint} setAllowPrint={setAllowPrint}
        />
      </div>
    </Modal>
  );
}

function ManageLinkModal({ open, link, itemName, updating, onClose, onUpdate, onRequestDelete }: {
  open: boolean; link: SharedLink; itemName: string; updating: boolean;
  onClose: () => void; onUpdate: (opts: UpdateLinkOptions) => void; onRequestDelete: () => void;
}) {
  const { toast } = useAppStore();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [name, setName] = useState(link.name ?? '');
  const [passwordOn, setPasswordOn] = useState(link.password_protected);
  const [password, setPassword] = useState('');
  const [expiryOn, setExpiryOn] = useState(!!link.expires_at);
  const [expiresAt, setExpiresAt] = useState(toDatetimeLocal(link.expires_at));
  const [allowDownload, setAllowDownload] = useState(link.allow_download);
  const [allowPrint, setAllowPrint] = useState(link.allow_print);

  // Re-sync the form from the link's current server state each time the modal
  // opens or a different link is passed in — not on every render, so typing
  // isn't clobbered mid-edit.
  useEffect(() => {
    if (!open) return;
    setSettingsOpen(false);
    setName(link.name ?? '');
    setPasswordOn(link.password_protected);
    setPassword('');
    setExpiryOn(!!link.expires_at);
    setExpiresAt(toDatetimeLocal(link.expires_at));
    setAllowDownload(link.allow_download);
    setAllowPrint(link.allow_print);
    // link.id (not the whole object) so this doesn't refire on every
    // background refetch of the same link's view_count.
  }, [open, link.id]);

  const url = `${window.location.origin}/s/${link.token}`;

  function handleUpdate() {
    onUpdate({
      name: name.trim() || null,
      allow_download: allowDownload,
      allow_print: allowPrint,
      // Explicit null clears; a password left blank while the toggle is off
      // means "no change" only when it was already off — otherwise clear it.
      password: passwordOn ? (password ? password : undefined) : null,
      expires_at: expiryOn ? (fromDatetimeLocal(expiresAt) ?? null) : null,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Manage share link" size="wide">
      <div className="t-stack">
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{itemName}</div>

        <div style={{ display: 'flex', gap: 6 }}>
          <input className="t-input t-input--sm" readOnly value={url} style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11 }} />
          <Button kind="outline" size="sm" icon="copy" onClick={() => navigator.clipboard.writeText(url).then(() => toast('Copied!'))}>Copy</Button>
          <Button kind="outline" size="sm" icon="mail" disabled title="Email sharing isn't available yet">Email link</Button>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div className="t-field" style={{ flex: 1 }}>
            <label>Link name</label>
            <input className="t-input t-input--sm" placeholder="e.g. Client review" value={name} onChange={e => setName(e.target.value)} maxLength={255} />
          </div>
          <div className="t-field" style={{ width: 130, flexShrink: 0 }}>
            <label>Access level</label>
            <div className="t-input t-input--sm" style={{ display: 'flex', alignItems: 'center', color: 'var(--ink-3)', cursor: 'default' }}>
              {PERMISSION_LABEL[link.permission]}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 14, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
          <span>{link.view_count} views</span>
          <span>{link.download_count} downloads</span>
          {link.password_protected && <span>Password protected</span>}
          {link.expires_at && <span>Expires {new Date(link.expires_at).toLocaleString()}</span>}
        </div>

        <button
          type="button"
          onClick={() => setSettingsOpen(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', color: 'var(--accent)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}
        >
          <Icon name={settingsOpen ? 'chev-d' : 'chev-r'} size={13} />
          Link settings
        </button>

        {settingsOpen && (
          <LinkFields
            passwordOn={passwordOn} setPasswordOn={setPasswordOn}
            password={password} setPassword={setPassword}
            expiryOn={expiryOn} setExpiryOn={setExpiryOn}
            expiresAt={expiresAt} setExpiresAt={setExpiresAt}
            allowDownload={allowDownload} setAllowDownload={setAllowDownload}
            allowPrint={allowPrint} setAllowPrint={setAllowPrint}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <Button kind="danger" size="sm" icon="trash" onClick={onRequestDelete}>Delete Share Link</Button>
          <Button kind="accent" size="sm" loading={updating} onClick={handleUpdate}>Update</Button>
        </div>
      </div>
    </Modal>
  );
}

// Zoho-style "Access Stats": per-guest view/download counts for one external
// link. Guests are anonymous (no account, no email) — the guest_label is a
// stable "Guest #N" ordinal, not an identity.
function LinkStatsModal({ open, link, itemName, stats, onClose }: {
  open: boolean; link: SharedLink; itemName: string; stats: LinkStats | undefined; onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="Access Stats" size="wide">
      <div className="t-stack">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
          <Icon name="link" size={13} />
          <span>{itemName}</span>
          {link.name && <><span>·</span><span style={{ fontWeight: 600, color: 'var(--ink)' }}>{link.name}</span></>}
        </div>

        {!stats ? (
          <div className="t-skeleton" style={{ height: 60, borderRadius: 8 }} />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14 }}>
              <StatCard icon="user" label="External Users" value={stats.external_users} />
              <StatCard icon="eye" label="Views" value={stats.views} />
              <StatCard icon="download" label="Downloads" value={stats.downloads} />
            </div>

            {stats.accesses.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', textAlign: 'center', padding: '20px 0' }}>
                No one has opened this link yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      <th style={{ padding: '6px 8px' }}>Accessed by &amp; time</th>
                      <th style={{ padding: '6px 8px' }}>Email</th>
                      <th style={{ padding: '6px 8px' }}>Views</th>
                      <th style={{ padding: '6px 8px' }}>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.accesses.map((a, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar name={a.guest_label} size="xs" />
                            <div>
                              <div style={{ fontWeight: 500 }}>{a.guest_label}</div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{relativeTime(a.last_seen_at)}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '8px', color: 'var(--ink-3)' }}>-</td>
                        <td style={{ padding: '8px' }}>{a.views}</td>
                        <td style={{ padding: '8px' }}>{a.downloads}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function StatCard({ icon, label, value }: { icon: string; label: string; value: string | number }) {
  return (
    <div style={{ flex: 1, padding: '12px 14px', background: 'var(--paper-2)', borderRadius: 10, border: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 'var(--fs-xs)', marginBottom: 4 }}>
        <Icon name={icon} size={13} />
        {label}
      </div>
      <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

// Replaces the comment textarea for anyone below 'commenter' — explains why,
// and lets them ask the owner/manager to raise their tier instead of just
// hitting a wall. Shown for viewer-tier grantees, org members without their
// own grant, and anyone else whose effective permission tops out at 'viewer'.
// Shown in the Sharing tab to anyone below manager tier — lets them see
// their own access and ask the owner/a manager for a different one, instead
// of the comment box silently rejecting them with no way to act on it.
function RequestAccessCard({ myPermission, pendingRequest, sending, onRequest }: {
  myPermission: SharePermission | undefined;
  pendingRequest: PermissionRequest | null;
  sending: boolean;
  onRequest: (permission: SharePermission, reason?: string) => void;
}) {
  const nextTierUp = PERMISSION_OPTIONS.find(p => !hasPermissionAtLeast(myPermission, p.value))?.value ?? 'manager';
  const [permission, setPermission] = useState<SharePermission>(nextTierUp);
  const [reason, setReason] = useState('');

  if (pendingRequest) {
    return (
      <div className="t-alert t-alert--info" style={{ alignItems: 'flex-start', gap: 10 }}>
        <Icon name="clock" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          You have <strong>{PERMISSION_LABEL[myPermission ?? 'viewer']}</strong> access.
          <br />
          Request for <strong>{PERMISSION_LABEL[pendingRequest.requested_permission]}</strong> sent — waiting on the owner or a manager.
        </span>
      </div>
    );
  }

  return (
    <div className="t-alert t-alert--warn" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Icon name="lock" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>Only the owner or a manager can change sharing for this file. You have <strong>{PERMISSION_LABEL[myPermission ?? 'viewer']}</strong> access.</span>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <select className="t-input t-input--sm" style={{ width: 150, flexShrink: 0 }} value={permission} onChange={e => setPermission(e.target.value as SharePermission)}>
          {PERMISSION_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <input
          className="t-input t-input--sm"
          style={{ flex: 1 }}
          placeholder="Reason (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <Button kind="accent" size="sm" loading={sending} onClick={() => onRequest(permission, reason.trim() || undefined)}>
          Request
        </Button>
      </div>
    </div>
  );
}

// Manager-only: pending asks from people below the tier they want, shown in
// the Sharing tab. Approving upserts the grant via the same code path as
// adding someone directly; denying just closes out the request.
function IncomingRequests({ requests, deciding, onDecide }: {
  requests: PermissionRequest[];
  deciding: boolean;
  onDecide: (requestId: string, decision: 'approve' | 'deny') => void;
}) {
  if (requests.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 10 }}>
        Pending requests
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {requests.map(r => (
          <div key={r.id} style={{ padding: '10px 12px', background: 'var(--paper-2)', borderRadius: 8, border: '1px solid var(--line)' }}>
            <div style={{ fontSize: 'var(--fs-sm)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{r.requested_by}</span>
              {' wants '}<strong>{PERMISSION_LABEL[r.requested_permission]}</strong>
              {r.current_permission && <> (currently {PERMISSION_LABEL[r.current_permission]})</>}
            </div>
            {r.reason && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 3 }}>“{r.reason}”</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Button kind="outline" size="sm" disabled={deciding} onClick={() => onDecide(r.id, 'deny')}>Deny</Button>
              <Button kind="accent" size="sm" loading={deciding} onClick={() => onDecide(r.id, 'approve')}>Approve</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
